import type { EppoAssignment } from '$lib/utils/eppo';
import type { StripePriceData } from '$lib/utils/stripe';
import type { ISbRichtext, ISbStoryData, SbBlokData } from '@storyblok/js';

export interface SbLink {
	cached_url: string;
	fieldtype: string;
	id: string;
	linktype: 'url' | 'story';
	url: string;
	target: string;
}

export type SbColor =
	| 'green-1'
	| 'green-2'
	| 'green-3'
	| 'green-4'
	| 'green-5'
	| 'green-6'
	| 'brown-1'
	| 'brown-2'
	| 'brown-3'
	| 'brown-6'
	| 'stone-1'
	| 'stone-2'
	| 'stone-3'
	| 'stone-4'
	| 'stone-5'
	| 'teal-1'
	| 'teal-2'
	| 'teal-3'
	| 'teal-4'
	| 'teal-5'
	| 'purple-1'
	| 'purple-2'
	| 'purple-3'
	| 'purple-4'
	| 'purple-5'
	| 'orange-1'
	| 'orange-2'
	| 'orange-3'
	| 'orange-4'
	| 'orange-5'
	| 'white'
	| 'dark-blue'
	| 'green-olive';

export type ColorTheme = 'light' | 'dark';

export type SbGridCol = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12';

export type SbGridColMobile = Extract<SbGridCol, '1' | '2' | '3' | '4'>;

export type SbImageFormat = '1/1' | '4/3' | '16/9' | '2/3' | '3/4';

export type SbOverlayPercentage = '0' | '12' | '24' | '40';

export interface SbValueProp extends SbBlokData {
	icon: SbImage;
	text?: string;
	title: string;
	center_text?: boolean;
}

export interface SbImage extends SbBlokData {
	filename: string;
	alt: string;
	title: string;
}

export interface SbVideo extends SbBlokData {
	component: 'mux_video';
	playback_id: string;
	loop: boolean;
	autoplay: boolean;
	mute: boolean;
}

export interface SbMuxVideo extends SbVideo {
	controls_position: 'bottom-right' | 'bottom-center' | 'top-right' | 'bottom-left';
	control_mute: boolean;
	control_play: boolean;
	control_fullscreen: boolean;
	control_progress_circle: boolean;
	video_click_action: 'mute' | 'pause' | 'fullscreen';
	control_fullscreen_top_right: boolean;
	control_play_bottom_right: boolean;
	rounded_corners: string;
	fit: 'cover' | 'contain';
}

export interface SbResponsiveMuxVideo extends SbBlokData {
	asset_mobile: Array<ISbStoryData<SbMuxVideo>['content']>;
	asset_desktop: Array<ISbStoryData<SbMuxVideo>['content']>;
}

export interface SbSeoMetadata {
	plugin: 'seo_metatags';
	title: string;
	description: string;
	og_title: string;
	og_description: string;
	og_image: string;
	twitter_title: string;
	twitter_description: string;
	twitter_image: string;
}

interface SbBlogArticleCategory {
	name: string;
	hero_background_color: string;
	hero_badge_color: string;
	hero_badge_text_color: string;
	list_badge_color: string;
	list_text_color: string;
	highlighted_article: SbBlogArticleStory | null;
}

export type SbBlogArticleCategoryStory = ISbStoryData<SbBlogArticleCategory>;

export type SbBlogArticle = SbBlokData & { seo?: SbSeoMetadata } & {
	title: string;
	subtitle: string;
	hero_image?: SbImage;
	category: SbBlogArticleCategoryStory | null;
	custom_publish_date: string;
	author: string;
	body: ISbStoryData[];
};

export type SbBlogArticleStory = ISbStoryData<SbBlogArticle & { component: 'blog_page' }>;

interface SbBlogExternalArticle {
	title: string;
	subtitle: string;
	link_title: string;
	link: SbLink;
	hero_image: SbImage;
}

export type SbBlogExternalArticleStory = ISbStoryData<
	SbBlogExternalArticle & { component: 'blog_external_article' }
>;

export function isExternalArticle(
	story: SbBlogArticleStory | SbBlogExternalArticleStory
): story is SbBlogExternalArticleStory {
	return story.content.component === 'blog_external_article';
}

export interface TeamMember extends SbBlokData {
	name: string;
	title: string;
	image: SbImage;
}

export type SbLandingPage = { seo?: SbSeoMetadata } & {
	header: Array<ISbStoryData>;
	body: Array<ISbStoryData>;
	footer: Array<ISbStoryData>;
	no_index: boolean;
};

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type ButtonColor =
	| 'green'
	| 'stone'
	| 'brown'
	| 'nyc-white-red'
	| 'nyc-white-blue'
	| 'nyc-dark';
export type ButtonSize = 'small' | 'medium' | 'large';
type IconPosition = 'before' | 'after';

export type SbButton = {
	mobile_size: ButtonSize;
	desktop_size: ButtonSize;
	variant: ButtonVariant;
	color: ButtonColor;
	label: string;
	title: string;
	link: SbLink;
	icon: SbImage;
	icon_position: IconPosition;
	opens_overlay: boolean;
	overlay_blocks: Array<ISbStoryData>;
	opens_cart: boolean;
	alignment?: 'center' | 'left' | 'right';
};

export type HeaderVariant = 'transparent-green' | 'transparent-white';

export type SbLandingPageHeroVariant = 'full-bleed' | 'image-top' | 'image-bottom';

export type TextAlignment = 'left' | 'center';

export type SbTextColor = 'brown-2' | 'stone-5' | 'green-1';

export type GapSize = 'xs' | 'small' | 'medium' | 'large';

export type SpacerSize =
	| 'none'
	| 'small'
	| 'medium'
	| 'large'
	| '16'
	| '20'
	| '28'
	| '32'
	| '36'
	| '40'
	| '56'
	| '80'
	| '100'
	| '114'
	| 'XXS'
	| 'XS'
	| 'S'
	| 'M'
	| 'L';

export interface GlobalCartData {
	title?: string;
	global_coupon_code?: string;
	default_product_bundle?: string;
	default_stripe_product?: StripeProductData;
	body?: Array<ISbStoryData>;
	below_cta_content?: Array<ISbStoryData>;
	display_taxes?: boolean;
	display_shipping?: boolean;
	display_discount?: boolean;
	display_affirm?: boolean;
	original_shipping_value?: string;
	display_cta_lock_icon?: boolean;
	cta_label?: string;
	processing_label?: string;
	shipping_id?: string;
	shipping_id_stage?: string;
	shipping_text?: ISbRichtext;
	is_trial?: boolean;
	user_label?: string;
	partner_source?: string;
	terms_of_service_text?: string;
	after_submit_text?: string;
	success_page?: ISbStoryData;
	price_subline?: ISbRichtext;
	force_bundle?: boolean;
	force_product?: boolean;
	enable_quantity_selection?: boolean;
	max_quantity?: string;
}

export interface EppoData extends SbBlokData {
	component: 'eppo_variant';
	show_in_editor: boolean;
	subject: 'anonymousId' | 'commutingZoneId' | 'state';
	url_attribute?: string;
	content: ISbStoryData[];
	eppo: {
		feature_flag: {
			id: number;
			key: string;
		};
		variant: {
			id: number;
			key: string;
		};
		assignment: EppoAssignment;
	};
}

export interface StripeProductData {
	priceLookupKey: string;
	data: StripePriceData;
}

export interface VerifiedReview {
	score: number;
	title: string;
	content: string;
	name: string;
	verified: boolean;
	city?: string;
	avatar?: SbImage;
}

interface SbTableEntry {
	value: string;
}

interface SbTableRow {
	body: Array<SbTableEntry>;
}

export interface SbTable {
	thead: Array<SbTableEntry>;
	tbody: Array<SbTableRow>;
}
export type SbOverlay = '12' | '24' | '40';

export type FinancingOption = 'all' | 'card_only';
